/* eslint-disable no-unused-vars */
import {
  Box,
  Button,
  fade,
  Grid,
  makeStyles,
} from '@material-ui/core';
import React, { useContext, useEffect, useMemo } from 'react';
import { AppRouteContext } from 'contexts';
import { useParams } from 'react-router-dom';
import { get } from 'lodash';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { ActivePatientContext } from '../../../contexts/ActivePatientProvider';
import { checkPermission } from '../../../helpers/checkPermissions';
import permissions from '../../../config/permissions';
import { PATIENT_TABLE_NAMES, ALL_ROUTES } from '../../../config/routes';
import AddButton from './AddButton';
import debounce from 'lodash/debounce';

const useStyles = makeStyles((theme) => ({
  historyBox: {
    paddingLeft: 4,
    paddingRight: 4,
    marginTop: -16,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    // width: 560,
  },
  greyText: {
    fontSize: 15,
    fontWeight: 400,
    fontStyle: 'italic',
    color: fade(theme.colors.blue2, 0.5),
  },
  flex: {
    display: 'flex',
    borderRadius: 17,
  },
  flexFullW: {
    display: 'flex',
    borderRadius: 17,
    maxWidth: 'fit-content',
    width: 'fit-content',
    transition: 'opacity 1s ease-out, width .3s ease-out',
  },
  blueText: {
    fontSize: 22,
    fontWeight: 400,
    color: theme.colors.lightBlue,
    paddingRight: theme.spacing(4),
  },
  tabItem: {
    minWidth: 30,
    transition: '.3s',
    fontSize: 15,
    boxShadow: 'none',
    borderRadius: 0,
    color: theme.colors.lightBlue,
    borderColor: theme.colors.lightBlue,
    backgroundColor: theme.palette.common.white,
    display: 'flex',
  },
  firstTab: {
    borderRightWidth: 1,
    borderTopLeftRadius: 17,
    borderBottomLeftRadius: 17,
  },
  lastTab: {
    borderLeftWidth: 0,
    borderTopRightRadius: 17,
    borderBottomRightRadius: 17,
  },
  tab: {
    borderRightWidth: 1,
    borderLeftWidth: 0,

    '&:hover': {
      borderRightWidth: 1,
      borderLeftWidth: 0,
    },
  },
  activeButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.colors.lightBlue,
    transition: '1s',
  },
  valueContainer: {
    display: 'flex',
    width: '100%',
    minHeight: 26,
    minWidth: 6,
    transition: '0s',
  },
  shortText: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    alignItems: 'center',
    right: 0,
    left: 0,
    justifyContent: 'center',
    transition: '0s',
  },
}));

let isShortTabName = null;

const PatientAdditionalNode = ({
                                 activeTableName,
                                 toggleAssessmentModal,
                                 toggleResourceModal,
                                 setOrderPatientUuid,
                                 toggleProgramModal,
                                 tabs,
                                 tableKey,
                               }) => {
  const classes = useStyles();
  const { en: { btn, titles } } = useSelector((state) => state.i18n);
  const { setAppRoute } = useContext(AppRouteContext);
  const { patientInfo } = useContext(ActivePatientContext);
  const containerElement = React.useRef();
  const {
    currentAccountUuid: paramCurrentAccountUuid,
    accountUuid: paramAccountUuid,
    patientUuid: paramsPatientUuid,
  } = useParams();
  const {
    metadata: { currentAccountPermissions, childAccountPermissions },
    isDistributionSponsor,
  } = useSelector((state) => state.user);

  const patientFirstName = get(patientInfo, 'firstName', '');
  const patientLastName = get(patientInfo, 'lastName', '');
  const isClaimsAvailable = checkPermission(
    isDistributionSponsor ? childAccountPermissions : currentAccountPermissions,
    permissions.CLAIMS_GET,
  );
  const isClaimReportAvailable = checkPermission(
    currentAccountPermissions,
    permissions.CLAIM_REPORTS_GET,
  ) && false; // remove if claim Report List enabled to patient page;
  const isResourcesAvailable = checkPermission(
    currentAccountPermissions,
    permissions.GET_RESOURCES,
  );
  const isProgramsAvailable = true; //checkPermission(
  // currentAccountPermissions,
  // permissions.ADMIN_PATIENT_IMPORT,
  // );

  const changeTable = (tableName) => {
    const key = tableKey.endsWith('Dashboard') ? tableKey.slice(0, -9) : tableKey;
    setAppRoute({
      title: `${titles[tableKey]}: ${patientFirstName} ${patientLastName}`,
      path: (paramAccountUuid ? ALL_ROUTES[key].dashboardDS : ALL_ROUTES[key].dashboard).path,
      params: {
        currentAccountUuid: paramCurrentAccountUuid,
        accountUuid: paramAccountUuid,
        patientUuid: paramsPatientUuid,
        tableName: tableName,
      },
    });
  };

  const updateShortName = debounce((v) => {
    isShortTabName = v;
    Array.from(document.getElementsByClassName('tabItem')).forEach(e => {
      setTimeout(() => {
        e.style.display = 'flex';
        e.style.opacity = 1;
      }, 100);
    });
  }, 300);

  const data = useMemo(
    () => [
      {
        tabName: PATIENT_TABLE_NAMES.assessment,
        path: PATIENT_TABLE_NAMES.assessment,
        value: btn.assessments,
        isAvailable: tabs.includes(PATIENT_TABLE_NAMES.assessment),
        width: 130,
      },
      {
        tabName: PATIENT_TABLE_NAMES.resource,
        path: PATIENT_TABLE_NAMES.resource,
        value: btn.resource,
        isAvailable: isResourcesAvailable && tabs.includes(PATIENT_TABLE_NAMES.resource),
        width: 110,
      },
      {
        tabName: PATIENT_TABLE_NAMES.order,
        path: PATIENT_TABLE_NAMES.order,
        value: btn.orders,
        isAvailable: tabs.includes(PATIENT_TABLE_NAMES.order),
        width: 80,
      },
      {
        tabName: PATIENT_TABLE_NAMES.claim,
        path: PATIENT_TABLE_NAMES.claim,
        value: btn.claimsReporting,
        isAvailable: isClaimsAvailable && tabs.includes(PATIENT_TABLE_NAMES.claim),
        width: 82,
      },
      {
        tabName: PATIENT_TABLE_NAMES.claimReport,
        path: PATIENT_TABLE_NAMES.claimReport,
        value: btn.claimReport,
        isAvailable: isClaimReportAvailable && tabs.includes(PATIENT_TABLE_NAMES.claimReport),
        width: 82,
      },
      {
        tabName: PATIENT_TABLE_NAMES.program,
        activeNumber: 2
          + (isResourcesAvailable ? 1 : 0)
          + (isClaimsAvailable ? 1 : 0)
          + (isClaimReportAvailable ? 1 : 0),
        path: PATIENT_TABLE_NAMES.program,
        value: btn.programs,
        isAvailable: isProgramsAvailable && tabs.includes(PATIENT_TABLE_NAMES.program),
        width: 105,
      },
    ].filter(item => item.isAvailable),
    [isClaimReportAvailable, isClaimsAvailable, isResourcesAvailable, isProgramsAvailable],
  );

  const width = useMemo(
    () => data.reduce((accumulator, { width }) => accumulator + width, 52), // 52 - "add" button width
    [data],
  );

  useEffect(() => {
    if (containerElement && containerElement.current) {
      const resizeObserver = new ResizeObserver(() => {
        const _isShortTabName = containerElement && containerElement.current
          ? containerElement.current?.parentElement.clientWidth < width : false;

        if (_isShortTabName !== isShortTabName) {
          Array.from(document.getElementsByClassName('tabItem')).forEach(e => {
            e.style.display = 'none';
            e.style.opacity = 0;
          });
          isShortTabName = _isShortTabName;
          setTimeout(() => {
            Array.from(document.getElementsByClassName('tabItem')).forEach(e => {
              e.style.display = 'flex';
              Array.from(e.childNodes).forEach(el => {
                if (el.childNodes[0] && el.childNodes[0].childNodes) {
                  el.childNodes[0].childNodes[0].style.display = !_isShortTabName ? 'flex' : 'none';
                  el.childNodes[0].childNodes[1].style.display = !_isShortTabName ? 'none' : 'flex';
                }
              });
            });
            Array.from(document.getElementsByClassName('tabItem')).forEach(e => {
              setTimeout(() => {
                e.style.opacity = 1;
              }, 100);
            });
          }, 300);
          updateShortName(_isShortTabName);
        }
      });
      resizeObserver.observe(containerElement.current);
    }
    if (isShortTabName == null && containerElement && containerElement.current) {
      const _isShortTabName = containerElement.current?.parentElement.clientWidth < 500;
      Array.from(document.getElementsByClassName('tabItem')).forEach(e => {
        e.style.display = 'flex';
        Array.from(e.childNodes).forEach(el => {
          if (el.childNodes[0] && el.childNodes[0].childNodes) {
            el.childNodes[0].childNodes[0].style.display = !_isShortTabName ? 'flex' : 'none';
            el.childNodes[0].childNodes[1].style.display = !_isShortTabName ? 'none' : 'flex';
          }
        });
        e.style.opacity = 1;
      });
    }
  }, [containerElement, width]);

  const isAddButtonVisible = toggleAssessmentModal
    && toggleResourceModal
    && setOrderPatientUuid
    && toggleProgramModal;

  return (
    <Grid ref={containerElement} container justify='flex-end' className={classes.historyBox}
          style={{
            width: width,
          }}>
      <Grid item className={classes.flex} style={{
        maxWidth: isAddButtonVisible ? 'calc(100% - 60px)' : '100%',

      }}>
        <Box className={classes.flexFullW} justifyContent='flex-end'>
          {
            data.map(({
                        tabName,
                        path,
                        value,
                        className,
                      }, index) => <Button
                color='primary'
                variant={activeTableName === tabName ? 'contained' : 'outlined'}
                className={cx('tabItem',
                  classes.tabItem,
                  index === 0
                    ? classes.firstTab
                    : index === data.length - 1
                      ? classes.lastTab
                      : classes.tab,
                  {
                    [classes.activeButton]: activeTableName === tabName,
                  })}
                onClick={() => changeTable(path)}
              >
                <div className={classes.valueContainer}>
                  <div>{value}</div>
                  <div style={{ display: 'none' }}
                       className={classes.shortText}>{value.charAt(0)}</div>
                </div>
              </Button>,
            )
          }
        </Box>
      </Grid>

      {
        isAddButtonVisible && <AddButton
          toggleAssessmentModal={toggleAssessmentModal}
          toggleResourceModal={toggleResourceModal}
          setOrderPatientUuid={setOrderPatientUuid}
          toggleProgramModal={toggleProgramModal}
        />
      }
    </Grid>
  );
};

export default PatientAdditionalNode;

